import React, { useState } from 'react';
import {
  Box,
  Typography,
  Switch,
  FormControlLabel,
  Select,
  MenuItem,
  Paper,
  CircularProgress,
  Snackbar,
  Alert,
  Slider,
  Divider,
  Chip,
  FormControl,
  InputLabel,
  Tabs,
  Tab,
  RadioGroup,
  Radio,
} from '@mui/material';
import {
  Edit,
  Undo,
  Save,
  AutoAwesome,
  FormatSize,
  Reply,
  AttachFile,
} from '@mui/icons-material';
import { useSettingsStore } from '../../stores/settingsStore';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div hidden={value !== index} {...other}>
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

const ComposeSettings: React.FC = () => {
  const { settings, isLoading, isSaving, updateSettings } = useSettingsStore();
  const [tabValue, setTabValue] = useState(0);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({ 
    open: false, 
    message: '', 
    severity: 'success' 
  });

  // SettingsLayout already fetches settings

  const handleSettingChange = async (key: string, value: any) => {
    try {
      await updateSettings({ [key]: value });
      setSnackbar({ open: true, message: 'Settings saved', severity: 'success' });
    } catch {
      setSnackbar({ open: true, message: 'Failed to save settings', severity: 'error' });
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
        <CircularProgress size={32} color="primary" />
      </Box>
    );
  }

  const fontFamilies = [
    'Arial', 'Verdana', 'Times New Roman', 'Georgia', 'Courier New',
    'Roboto', 'Open Sans', 'Lato', 'Source Sans Pro', 'Inter'
  ];

  const undoSendOptions = [
    { value: 0, label: 'Disabled' },
    { value: 5, label: '5 seconds' },
    { value: 10, label: '10 seconds' },
    { value: 20, label: '20 seconds' },
    { value: 30, label: '30 seconds' },
  ];

  const autoSaveOptions = [
    { value: 30, label: 'Every 30 seconds' },
    { value: 60, label: 'Every minute' },
    { value: 120, label: 'Every 2 minutes' },
    { value: 300, label: 'Every 5 minutes' },
  ];

  return (
    <Box>
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={2000} 
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} variant="filled" sx={{ borderRadius: '8px' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
      
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
        <Typography variant="h5">Compose Settings</Typography>
        {isSaving && <CircularProgress size={16} color="primary" />}
      </Box>

      <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)} sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Tab icon={<FormatSize />} label="Formatting" iconPosition="start" />
        <Tab icon={<Edit />} label="Behavior" iconPosition="start" />
        <Tab icon={<AutoAwesome />} label="Smart Features" iconPosition="start" />
        <Tab icon={<AttachFile />} label="Attachments" iconPosition="start" />
      </Tabs>

      {/* Formatting Tab */}
      <TabPanel value={tabValue} index={0}>
        <Paper variant="outlined" sx={{ p: 2.5, mb: 3 }}>
          <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 500 }}>
            Default Text Formatting
          </Typography>

          <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', mb: 3 }}>
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>Font Family</InputLabel>
              <Select
                value={settings.defaultFontFamily || 'Arial'}
                label="Font Family"
                onChange={(e) => handleSettingChange('defaultFontFamily', e.target.value)}
              >
                {fontFamilies.map(font => (
                  <MenuItem key={font} value={font} sx={{ fontFamily: font }}>{font}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Font Size</InputLabel>
              <Select
                value={settings.defaultFontSize || 14}
                label="Font Size"
                onChange={(e) => handleSettingChange('defaultFontSize', Number(e.target.value))}
              >
                {[10, 11, 12, 13, 14, 16, 18, 20, 24].map(size => (
                  <MenuItem key={size} value={size}>{size}pt</MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Default Format</InputLabel>
              <Select
                value={settings.defaultComposeFormat || 'html'}
                label="Default Format"
                onChange={(e) => handleSettingChange('defaultComposeFormat', e.target.value)}
              >
                <MenuItem value="html">Rich Text (HTML)</MenuItem>
                <MenuItem value="plain">Plain Text</MenuItem>
              </Select>
            </FormControl>
          </Box>

          <Box sx={{ p: 2, bgcolor: 'action.hover', borderRadius: '8px', border: '1px solid', borderColor: 'divider' }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              Preview:
            </Typography>
            <Typography
              sx={{
                fontFamily: settings.defaultFontFamily || 'Arial',
                fontSize: `${settings.defaultFontSize || 14}pt`,
                color: 'text.primary',
              }}
            >
              This is a preview of your default text formatting.
            </Typography>
          </Box>
        </Paper>

        <Paper variant="outlined" sx={{ p: 2.5 }}>
          <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 500 }}>
            Compose Window
          </Typography>

          <FormControlLabel
            control={
              <Switch
                checked={settings.fullScreenCompose ?? false}
                onChange={(e) => handleSettingChange('fullScreenCompose', e.target.checked)}
              />
            }
            label={
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>Full screen compose</Typography>
                <Typography variant="caption" color="text.secondary">
                  Open compose window in full screen by default
                </Typography>
              </Box>
            }
          />

          <Divider sx={{ my: 2 }} />

          <FormControlLabel
            control={
              <Switch
                checked={settings.showToolbar ?? true}
                onChange={(e) => handleSettingChange('showToolbar', e.target.checked)}
              />
            }
            label={
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>Show formatting toolbar</Typography>
                <Typography variant="caption" color="text.secondary">
                  Display formatting options while composing
                </Typography>
              </Box>
            }
          />
        </Paper>
      </TabPanel>

      {/* Behavior Tab */}
      <TabPanel value={tabValue} index={1}>
        <Paper variant="outlined" sx={{ p: 2.5, mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Undo color="primary" />
            <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
              Undo Send
            </Typography>
            <Chip label="Recommended" size="small" color="success" />
          </Box>
          
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Give yourself time to cancel a sent email if you spot a mistake.
          </Typography>

          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Cancellation Period</InputLabel>
            <Select
              value={settings.undoSendSeconds ?? 10}
              label="Cancellation Period"
              onChange={(e) => handleSettingChange('undoSendSeconds', Number(e.target.value))}
            >
              {undoSendOptions.map(opt => (
                <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Paper>

        <Paper variant="outlined" sx={{ p: 2.5, mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Reply color="primary" />
            <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
              Reply Behavior
            </Typography>
          </Box>

          <FormControl size="small" sx={{ minWidth: 250, mb: 2 }}>
            <InputLabel>Default Reply Action</InputLabel>
            <Select
              value={settings.defaultReplyBehavior || 'reply'}
              label="Default Reply Action"
              onChange={(e) => handleSettingChange('defaultReplyBehavior', e.target.value)}
            >
              <MenuItem value="reply">Reply (only to sender)</MenuItem>
              <MenuItem value="replyAll">Reply All (include all recipients)</MenuItem>
            </Select>
          </FormControl>

          <Divider sx={{ my: 2 }} />

          <FormControlLabel
            control={
              <Switch
                checked={settings.includeOriginalInReply ?? true}
                onChange={(e) => handleSettingChange('includeOriginalInReply', e.target.checked)}
              />
            }
            label={
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>Include original message in reply</Typography>
                <Typography variant="caption" color="text.secondary">
                  Quote the original email when replying
                </Typography>
              </Box>
            }
          />
        </Paper>

        <Paper variant="outlined" sx={{ p: 2.5, mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Save color="primary" />
            <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
              Auto-Save
            </Typography>
          </Box>

          <FormControl size="small" sx={{ minWidth: 200, mb: 2 }}>
            <InputLabel>Auto-save drafts</InputLabel>
            <Select
              value={settings.autoSaveInterval ?? 60}
              label="Auto-save drafts"
              onChange={(e) => handleSettingChange('autoSaveInterval', Number(e.target.value))}
            >
              {autoSaveOptions.map(opt => (
                <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Paper>

        <Paper variant="outlined" sx={{ p: 2.5 }}>
          <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 500 }}>
            Send Options
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={settings.enableSpellCheck ?? true}
                  onChange={(e) => handleSettingChange('enableSpellCheck', e.target.checked)}
                />
              }
              label={
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>Enable spell check</Typography>
                  <Typography variant="caption" color="text.secondary">Check spelling as you type</Typography>
                </Box>
              }
            />

            <FormControlLabel
              control={
                <Switch
                  checked={settings.sendOnCtrlEnter ?? false}
                  onChange={(e) => handleSettingChange('sendOnCtrlEnter', e.target.checked)}
                />
              }
              label={
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>Send with Ctrl+Enter</Typography>
                  <Typography variant="caption" color="text.secondary">Use keyboard shortcut to send emails</Typography>
                </Box>
              }
            />

            <FormControlLabel
              control={
                <Switch
                  checked={settings.confirmBeforeSend ?? false}
                  onChange={(e) => handleSettingChange('confirmBeforeSend', e.target.checked)}
                />
              }
              label={
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>Confirm before sending</Typography>
                  <Typography variant="caption" color="text.secondary">Show confirmation dialog before sending</Typography>
                </Box>
              }
            />

            <FormControlLabel
              control={
                <Switch
                  checked={settings.warnEmptySubject ?? true}
                  onChange={(e) => handleSettingChange('warnEmptySubject', e.target.checked)}
                />
              }
              label={
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>Warn about empty subject</Typography>
                  <Typography variant="caption" color="text.secondary">Alert when sending without a subject</Typography>
                </Box>
              }
            />

            <FormControlLabel
              control={
                <Switch
                  checked={settings.warnNoAttachment ?? true}
                  onChange={(e) => handleSettingChange('warnNoAttachment', e.target.checked)}
                />
              }
              label={
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>Warn about missing attachments</Typography>
                  <Typography variant="caption" color="text.secondary">Alert if "attached" is mentioned but no file attached</Typography>
                </Box>
              }
            />
          </Box>
        </Paper>
      </TabPanel>

      {/* Smart Features Tab */}
      <TabPanel value={tabValue} index={2}>
        <Paper variant="outlined" sx={{ p: 2.5, mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <AutoAwesome sx={{ color: 'warning.main' }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
              Smart Compose
            </Typography>
            <Chip label="AI Powered" size="small" color="warning" />
          </Box>
          
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Get writing suggestions as you compose emails, powered by AI.
          </Typography>

          <FormControlLabel
            control={
              <Switch
                checked={settings.smartCompose ?? true}
                onChange={(e) => handleSettingChange('smartCompose', e.target.checked)}
              />
            }
            label={
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>Enable Smart Compose</Typography>
                <Typography variant="caption" color="text.secondary">
                  Get AI-powered writing suggestions
                </Typography>
              </Box>
            }
          />

          {settings.smartCompose && (
            <Box sx={{ ml: 6, mt: 2 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.smartComposePersonalization ?? true}
                    onChange={(e) => handleSettingChange('smartComposePersonalization', e.target.checked)}
                  />
                }
                label={
                  <Box>
                    <Typography variant="body2">Personalize suggestions</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Learn from your writing style
                    </Typography>
                  </Box>
                }
              />
            </Box>
          )}
        </Paper>

        <Paper variant="outlined" sx={{ p: 2.5, mb: 3 }}>
          <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 500 }}>
            Smart Reply
          </Typography>
          
          <FormControlLabel
            control={
              <Switch
                checked={settings.smartReply ?? true}
                onChange={(e) => handleSettingChange('smartReply', e.target.checked)}
              />
            }
            label={
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>Show smart reply suggestions</Typography>
                <Typography variant="caption" color="text.secondary">
                  Get quick reply options based on email content
                </Typography>
              </Box>
            }
          />
        </Paper>

        <Paper variant="outlined" sx={{ p: 2.5, mb: 3 }}>
          <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 500 }}>
            Grammar & Style
          </Typography>
          
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={settings.grammarCheck ?? true}
                  onChange={(e) => handleSettingChange('grammarCheck', e.target.checked)}
                />
              }
              label={
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>Grammar suggestions</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Get suggestions for grammar improvements
                  </Typography>
                </Box>
              }
            />

            <FormControlLabel
              control={
                <Switch
                  checked={settings.styleCheck ?? false}
                  onChange={(e) => handleSettingChange('styleCheck', e.target.checked)}
                />
              }
              label={
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>Writing style suggestions</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Get tips for clearer, more professional writing
                  </Typography>
                </Box>
              }
            />
          </Box>
        </Paper>

        <Paper variant="outlined" sx={{ p: 2.5 }}>
          <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 500 }}>
            Auto-Complete
          </Typography>
          
          <FormControlLabel
            control={
              <Switch
                checked={settings.autoCompleteContacts ?? true}
                onChange={(e) => handleSettingChange('autoCompleteContacts', e.target.checked)}
              />
            }
            label={
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>Auto-complete email addresses</Typography>
                <Typography variant="caption" color="text.secondary">
                  Suggest contacts as you type in To, Cc, Bcc fields
                </Typography>
              </Box>
            }
          />
        </Paper>
      </TabPanel>

      {/* Attachments Tab */}
      <TabPanel value={tabValue} index={3}>
        <Paper variant="outlined" sx={{ p: 2.5, mb: 3 }}>
          <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 500 }}>
            Attachment Settings
          </Typography>
          
          <Box sx={{ mb: 3 }}>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Maximum attachment size: <strong>{settings.maxAttachmentSize || 25} MB</strong>
            </Typography>
            <Slider
              value={settings.maxAttachmentSize || 25}
              onChangeCommitted={(_, value) => handleSettingChange('maxAttachmentSize', value)}
              min={5}
              max={50}
              step={5}
              marks={[
                { value: 5, label: '5MB' },
                { value: 25, label: '25MB' },
                { value: 50, label: '50MB' },
              ]}
              valueLabelDisplay="auto"
              valueLabelFormat={(value) => `${value}MB`}
              sx={{ maxWidth: 400 }}
            />
          </Box>

          <Divider sx={{ my: 2 }} />

          <FormControlLabel
            control={
              <Switch
                checked={settings.compressLargeAttachments ?? true}
                onChange={(e) => handleSettingChange('compressLargeAttachments', e.target.checked)}
              />
            }
            label={
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>Compress large attachments</Typography>
                <Typography variant="caption" color="text.secondary">
                  Automatically compress files over 10MB
                </Typography>
              </Box>
            }
          />

          <FormControlLabel
            control={
              <Switch
                checked={settings.useDriveForLargeFiles ?? true}
                onChange={(e) => handleSettingChange('useDriveForLargeFiles', e.target.checked)}
              />
            }
            label={
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>Use cloud storage for large files</Typography>
                <Typography variant="caption" color="text.secondary">
                  Upload large files to connected cloud storage and share links
                </Typography>
              </Box>
            }
          />
        </Paper>

        <Paper variant="outlined" sx={{ p: 2.5, mb: 3 }}>
          <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 500 }}>
            Security
          </Typography>
          
          <FormControlLabel
            control={
              <Switch
                checked={settings.stripExifData ?? true}
                onChange={(e) => handleSettingChange('stripExifData', e.target.checked)}
              />
            }
            label={
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>Remove image metadata</Typography>
                <Typography variant="caption" color="text.secondary">
                  Strip EXIF data (location, camera info) from photos before sending
                </Typography>
              </Box>
            }
          />

          <FormControlLabel
            control={
              <Switch
                checked={settings.virusScanAttachments ?? true}
                onChange={(e) => handleSettingChange('virusScanAttachments', e.target.checked)}
              />
            }
            label={
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>Scan attachments for viruses</Typography>
                <Typography variant="caption" color="text.secondary">
                  Automatically scan files before sending
                </Typography>
              </Box>
            }
          />
        </Paper>

        <Paper variant="outlined" sx={{ p: 2.5 }}>
          <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 500 }}>
            Image Handling
          </Typography>
          
          <Typography variant="body2" sx={{ mb: 1 }}>
            Default image resize for attachments
          </Typography>
          
          <RadioGroup
            value={settings.imageResizeOption || 'original'}
            onChange={(e) => handleSettingChange('imageResizeOption', e.target.value)}
          >
            <FormControlLabel
              value="original"
              control={<Radio size="small" />}
              label={
                <Box>
                  <Typography variant="body2">Original size</Typography>
                  <Typography variant="caption" color="text.secondary">Keep images at full resolution</Typography>
                </Box>
              }
            />
            <FormControlLabel
              value="large"
              control={<Radio size="small" />}
              label={
                <Box>
                  <Typography variant="body2">Large (1920px)</Typography>
                  <Typography variant="caption" color="text.secondary">Good for presentations</Typography>
                </Box>
              }
            />
            <FormControlLabel
              value="medium"
              control={<Radio size="small" />}
              label={
                <Box>
                  <Typography variant="body2">Medium (1024px)</Typography>
                  <Typography variant="caption" color="text.secondary">Balanced size and quality</Typography>
                </Box>
              }
            />
            <FormControlLabel
              value="small"
              control={<Radio size="small" />}
              label={
                <Box>
                  <Typography variant="body2">Small (640px)</Typography>
                  <Typography variant="caption" color="text.secondary">Faster to send</Typography>
                </Box>
              }
            />
          </RadioGroup>
        </Paper>
      </TabPanel>
    </Box>
  );
};

export default ComposeSettings;
